import React from 'react';
import {IMatchPairsReport} from '../../../../incase-domain/analytics';
import {IElementQuestionMatchPair} from '../../../../incase-domain/elements/question/questionMatchPair';
import ConfusionMatrix from '../../../../incase-domain/components/report/answer/matchPair/confusionMatrix/ConfusionMatrix';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import ReportAnswerMatchPair from '../../../../incase-domain/components/report/answer/matchPair/ReportAnswerMatchPair';

interface IMatchPairsReportProps {
    question: IElementQuestionMatchPair;
    device?: DeviceType;
}

function MatchPairsQuestionReport({question, device}: IMatchPairsReportProps): JSX.Element {
    const STATIC_MATCH_PERCENTAGES = [
        78, 23, 45, 67, 89, 34, 56, 12, 91, 65, 43, 87, 29, 76, 92, 18, 54, 71, 36, 47, 83, 62, 25, 94, 59, 72, 31, 86, 48,
    ];
    const STATIC_TOTAL_VALUES = [456, 723, 892, 345, 678, 534, 289, 567, 834, 412, 756, 623, 789, 134, 567, 890, 234, 445, 667, 888];

    function getStaticPercentage(index: number): number {
        return STATIC_MATCH_PERCENTAGES[index % STATIC_MATCH_PERCENTAGES.length];
    }

    function getStaticTotal(index: number): number {
        return STATIC_TOTAL_VALUES[index % STATIC_TOTAL_VALUES.length];
    }

    function createLabelMatrix(
        leftLabels: {id: string; text: string}[],
        rightLabels: {id: string; text: string}[],
        pairs: {leftElementId: string; rightElementId: string}[]
    ): Record<string, Record<string, {count: number; correct: boolean}>> {
        const matrix: Record<string, Record<string, {count: number; correct: boolean}>> = {};
        let percentageIndex = 0;

        leftLabels.forEach((leftLabel) => {
            matrix[leftLabel.text] = {};
            rightLabels.forEach((rightLabel) => {
                const percentage = getStaticPercentage(percentageIndex);

                const isCorrectPair = pairs.some((pair) => pair.leftElementId === leftLabel.id && pair.rightElementId === rightLabel.id);

                matrix[leftLabel.text][rightLabel.text] = {
                    count: percentage,
                    correct: isCorrectPair,
                };
                percentageIndex += 1;
            });
        });

        return matrix;
    }

    function createTotals(labels: string[]): Record<string, number> {
        const totals: Record<string, number> = {};

        labels.forEach((label, index) => {
            totals[label] = getStaticTotal(index);
        });

        return totals;
    }

    function getUniqueElements(elements: Array<{id: string; text: string}>): string[] {
        const uniqueMap = new Map<string, string>();

        elements.forEach((el) => {
            const key = `${el.id}-${el.text}`;
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, el.text);
            }
        });

        return Array.from(uniqueMap.values());
    }

    function generateMatchPairsReport(): IMatchPairsReport {
        const leftLabels = (question as IElementQuestionMatchPair).leftElement.map((el) => ({
            id: el.id,
            text: el.text,
        }));
        const rightLabels = (question as IElementQuestionMatchPair).rightElement.map((el) => ({
            id: el.id,
            text: el.text,
        }));

        const labelMatrix = createLabelMatrix(leftLabels, rightLabels, question.pairs);
        const totals = createTotals([...leftLabels.map((l) => l.text), ...rightLabels.map((r) => r.text)]);

        return {
            leftLabels: leftLabels.map((l) => l.text),
            rightLabels: getUniqueElements(rightLabels),
            labelMatrix,
            totals,
        };
    }

    const matchPairsReport = generateMatchPairsReport();
    return (
        <div className={`c-questionReport__match-pairs-report ${device === DeviceType.MOBILE} c-questionReport__match-pairs-report-mobile`}>
            {device === DeviceType.MOBILE ? (
                <ReportAnswerMatchPair matchPairReport={matchPairsReport} />
            ) : (
                <ConfusionMatrix matchPairReport={matchPairsReport} showPercentages />
            )}
        </div>
    );
}

export default MatchPairsQuestionReport;
